
import UserMessage from './user-message.vue'
import AIMessage from './ai-message.vue'
import ChatError from './chat-error.vue'
import UserCancelled from './user-cancelled.vue'
import AiAssistant from './ai-assistant'
import ChatInput from 'components/chat-input'
import AIAction from './ai-action.vue'

export default {
  components: {
    AiAssistant,
    UserMessage,
    UserCancelled,
    AIMessage,
    AIAction,
    ChatError,
    ChatInput
  },
  props: {
    chatId: {type: String, required: false, default: ''},
    generating: {type: Boolean, default: false},
    placeholder: {type: String, default: 'What can I help you with?'},
    history: {type: Array, default: () => []},
    description: {type: String, default: ''},
    mode: {type: String, default: 'expanded'},
    showInput: {type: Boolean, default: true},
    readonly: {type: Boolean, default: false},
    refHistoryLen: {type: Number, default: undefined}
  },
  emits: ['regenerate', 'stop', 'ask-ai', 'like', 'dislike', 'save', 'refrag', 'meeting-save', 'onboarding-completed', 'podcast-save', 'prompt-save'],
  data() {
    return {
      input_text: '',
      disabled: false,
      autoscroll: false,
      client_height: undefined,
      typing: false,
      question_position: 'visible',
    }
  },
  computed: {
    last_answer_idx() {
      return this.history.findLastIndex(x => x.type === 'assistant')
    },
    last_prompt_idx() {
      return this.history.findLastIndex(x => x.type === 'user')
    },
    last_answer(){
      return this.history[this.last_answer_idx]
    },
    ai_question() {
      return this.question_position !== 'visible' && this.last_answer?.data?.reasoning?.at(-1).input
    },
    input_disabled() {
      // if we're generating input is always enabled
      // user should be able to press the stop button
      if(this.generating) return false

      // if answer is still being typed input is disabled 
      if (this.typing) return true
      
      // input is disabled in onboarding if we have extracted info
      if (this.mode == 'onboarding' && this.last_answer?.data?.extracted_info) {
        return true
      }

      // input is disabled in any mode if we have controls waiting for user input
      if (this.last_answer?.data?.input) {
        return true
      }
      
      // input is disabled in any mode if last message is in an intermediate state
      // for example when there is an ai question in reasoning panel
      if (this.last_answer?.intermediate) {
        return true
      }

      return false
    },
  },
  watch: {
    async input_disabled(value) {
      if (value) return

      await this.$nextTick() 
      this.focus()      
    },
    'history.length': async function () {
      if (this.generating) {
        await this.$nextTick()
        this.scrollToEnd(true)

        if (this.mode !== 'expanded' || this.history.at(-1).type !== 'assistant') return

        // Check the viewport position relative to reasoning in the last answer. 
        const observer = this.getViewportObserver()
        observer.unobserveAll() 
        const node = this.$refs.history.at(-1).getAnswerTabs().$el
        observer.observe(node, 
          entry => this.question_position = entry.position
        )
      }
    }
  },
  async mounted() {
    this.setClientHeight()
    if (this.mode === 'expanded') {
      return
    }
    this.$nextTick(() => this.scrollToEnd(true))
  },
  methods: {
    getViewportObserver(){
      return this.$refs.scroller.getObserver()
    },
    getMinHeight(ind){
      const conditions = [this.mode === 'expanded', ind === this.history.length - 1, this.history[ind] !== 'user', this.client_height]
      if (conditions.some(x => !x)) return 'unset'
      const refs = this.$refs.history || []
      const last_prompt_h = refs[this.last_prompt_idx]?.$el.clientHeight || 0
      return (this.client_height - last_prompt_h) + 'px'
    },
    scrollToEnd(smooth = false) {
      // timer can call this after unmount
      this.$refs.scroller?.scrollToEnd(smooth)
    },
    onTextAdded(full) {
      this.typing = !full
      if (this.autoscroll) this.scrollToEnd(true)
    },
    setClientHeight(){
      if (this.$refs.scroller?.$el) {
        this.client_height = this.$refs.scroller.$el.clientHeight
      }
    },
    onWheel(ev) {
      // if user scrolls up we want to make force autoscroll stopping (with aborting requestAnimationFrame)
      // if we not do this, autoscroll will keep scrolling down because requestAnimationFrame still running
      const wheel_up = ev.deltaY < 0
      const scroller = this.$refs.scroller
      if (wheel_up) {
        this.autoscroll = false
        scroller.abortScrolling()
      }
    },
    onScroll() {
      // if we're not at the scroll end it means that user have scrolled manually. 
      // In this case we ignore autoscroll to not to annoy the user
      const scroller = this.$refs.scroller
      const on_bottom = scroller.checkBottom()
      this.autoscroll = !this.can_type && on_bottom
    },
    scrollToReasoning() {
      const node = this.$refs.history.at(-1)?.$el
      if (node) {
        node.scrollIntoView({behavior: 'smooth'})
      }
    },
    focus() {
      if (this.readonly) return
      this.$refs.input.focus()
    },
    stop(){
      this.typing = false
      this.$emit('stop')
    },
    onOnboardingCompleted(ev) {
      this.disabled = true
      this.$emit('onboarding-completed', ev)
    },
  }
}
